import { useState } from "react";
import { Download, ArrowRight } from "lucide-react";
import Reveal from "@/components/site/Reveal";

/* ------------------------------------------------------------------
 * Editorial Bookshelf · Kinetic Editorial
 * Magazin-Cover als physische Objekte in der DH-Designsprache:
 * League-Gothic-Header, Mono-Nummern 01–12, Terra-Hairlines.
 * ------------------------------------------------------------------ */

const SHELVES = [
  {
    label: "Recht & Delegation",
    kicker: "Grundwerk",
    items: [
      { slug: "wer-darf-was-atlas-vollversion", label: "Wer darf was? · Atlas · Vol. 1", pages: 251 },
      { slug: "haftungsfallen-pdl-vollversion", label: "Wo Haftung liegt · PDL & GF", pages: 42 },
      { slug: "kuerzungen-widerspruch-guide", label: "Kürzung? Widerspruch.", pages: 28 },
    ],
  },
  {
    label: "Prüfung & Qualität",
    kicker: "MD-Kompass",
    items: [
      { slug: "mdk-survival-guide", label: "MDK. Ohne Angst.", pages: 42 },
      { slug: "entbuerokratisierung-sis-checkliste", label: "Weniger Büro. Mehr Pflege.", pages: 18 },
      { slug: "erschoepfungs-dienstplan", label: "Dienstplan ohne Brand.", pages: 22 },
    ],
  },
  {
    label: "Abrechnung & Wirtschaft",
    kicker: "Cash-Werkzeug",
    items: [
      { slug: "abrechnungs-atlas-vollversion", label: "Jede Minute. Jede Ziffer.", pages: 32 },
      { slug: "pflegesatz-verhandlungs-leitfaden", label: "Der Pflegesatz gehört dir.", pages: 24 },
      { slug: "leistungsgruppen-lg1-lg2", label: "LG 1 & LG 2. Entschlüsselt.", pages: 14 },
    ],
  },
  {
    label: "Menschen & Beziehung",
    kicker: "Kommunikation",
    items: [
      { slug: "pflegegrad-spielbuch", label: "Pflegegrad-Spielbuch.", pages: 34 },
      { slug: "angehoerigen-beschwerde-leitfaden", label: "Ruhe findet Worte.", pages: 26 },
      { slug: "dh-katalog-2026", label: "Studio DH · Ausbildungs-Atlas 2026", pages: 48 },
    ],
  },
];

const THUMB = (slug) => `/downloads/thumbs/${slug}.jpg`;
const PDF = (slug) => `/downloads/${slug}.pdf`;

export default function EditorialBookshelf() {
  return (
    <section
      id="bookshelf"
      className="relative bg-cream-50 py-28 md:py-40 overflow-hidden"
      data-testid="editorial-bookshelf-section"
    >
      {/* subtle grain overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.035] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial header — Kinetic */}
        <Reveal>
          <div className="grid md:grid-cols-12 gap-6 items-end border-b border-sand-800/15 pb-10 mb-16">
            <div className="md:col-span-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono text-[11px] tracking-[0.2em] text-terra">Nº 09</span>
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-sand-500">
                  Studio DH · Bibliothek · 2026
                </span>
              </div>
              <h2 className="font-magazine font-medium text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] text-sand-800 leading-[1.08]">
                Die Redaktion<br />
                <span className="text-terra">der Praxis.</span>
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <p className="font-mono text-[11px] tracking-[0.12em] uppercase text-sand-600">
                12 Ausgaben · Digital Edition
              </p>
              <p className="text-[13px] text-sand-500 mt-2 leading-relaxed">
                Kostenlos im Helden-Basic-Campus — inklusive monatlichem Update.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Shelves */}
        <div className="space-y-24">
          {SHELVES.map((shelf, si) => (
            <Reveal key={shelf.label} delay={si * 60}>
              <div>
                {/* shelf label — editorial index style */}
                <div className="flex items-baseline gap-5 mb-10">
                  <span className="font-mono text-[11px] tracking-[0.2em] text-terra whitespace-nowrap">
                    Vol. {String(si + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[10.5px] tracking-[0.28em] font-bold text-sand-800 uppercase whitespace-nowrap">
                    {shelf.label}
                  </span>
                  <span className="h-px flex-1 bg-terra/30" />
                  <span className="text-[10px] tracking-[0.26em] text-sand-500 uppercase whitespace-nowrap hidden sm:block">
                    {shelf.kicker}
                  </span>
                </div>

                {/* the shelf itself */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14 items-end">
                  {shelf.items.map((item, i) => (
                    <BookCover
                      key={item.slug}
                      item={item}
                      n={si * 3 + i + 1}
                      tiltDir={i % 2 === 0 ? -1 : 1}
                    />
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Colophon */}
        <Reveal>
          <div className="mt-24 pt-10 border-t border-sand-800/15 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <p className="text-[14px] text-sand-600 max-w-xl leading-relaxed">
              Alle Magazine kostenlos im{" "}
              <strong className="text-sand-800">Helden-Basic-Campus</strong> —
              inklusive monatlichem Update, wenn eine neue Ausgabe erscheint.
            </p>
            <a
              href="/campus/login"
              className="inline-flex items-center gap-2 rounded-full bg-terra text-cream-50 px-7 py-3.5 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-espresso transition-colors duration-300"
              data-testid="bookshelf-campus-cta"
            >
              In den Campus einloggen
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function BookCover({ item, n, tiltDir = -1 }) {
  const [hover, setHover] = useState(false);

  return (
    <a
      href={PDF(item.slug)}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block outline-none"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`book-${item.slug}`}
      style={{ perspective: "1200px" }}
    >
      {/* Cover wrapper with 3D tilt */}
      <div
        className="relative transition-transform duration-500 ease-out will-change-transform"
        style={{
          transform: hover
            ? "translateY(-14px) rotateY(0deg) rotateZ(0deg) scale(1.03)"
            : `translateY(0) rotateY(${tiltDir * 6}deg) rotateZ(${tiltDir * -1.4}deg) scale(1)`,
          transformStyle: "preserve-3d",
        }}
      >
        {/* Spine shadow behind */}
        <div
          className="absolute inset-y-3 -left-1.5 w-3 rounded-l-sm"
          style={{
            background:
              "linear-gradient(90deg, rgba(27,26,24,0.55) 0%, rgba(27,26,24,0.15) 100%)",
            transform: "translateZ(-8px)",
          }}
        />

        {/* Cover image */}
        <div
          className="relative overflow-hidden rounded-sm shadow-[0_14px_40px_-14px_rgba(27,26,24,0.55)]"
          style={{
            aspectRatio: "0.707",
            background: "#1B1A18",
          }}
        >
          <img
            src={THUMB(item.slug)}
            alt={item.label}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          {/* subtle top-highlight */}
          <div
            className="absolute inset-x-0 top-0 h-1/4 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, transparent 100%)",
            }}
          />
          {/* hover CTA */}
          <div
            className={`absolute inset-x-0 bottom-0 p-3 flex items-center justify-between transition-all duration-300 ${
              hover ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
            }`}
            style={{
              background:
                "linear-gradient(180deg, transparent 0%, rgba(27,26,24,0.85) 60%)",
            }}
          >
            <span className="text-cream-50 font-mono text-[10px] tracking-[0.14em] uppercase">
              {item.pages} S. · PDF
            </span>
            <span className="inline-flex items-center gap-1 text-saffron font-mono text-[10px] tracking-[0.14em] uppercase">
              Ansehen <Download size={11} />
            </span>
          </div>
        </div>
      </div>

      {/* Caption — Kinetic typography */}
      <div className="mt-5 flex items-start gap-3">
        <span className="font-mono text-[11px] tracking-[0.14em] text-terra mt-0.5 whitespace-nowrap">
          {String(n).padStart(2, "0")}
        </span>
        <div>
          <p className="text-sand-800 text-[15px] font-semibold leading-snug group-hover:text-terra transition-colors">
            {item.label}
          </p>
          <p className="text-[10.5px] uppercase tracking-[0.2em] text-sand-500 mt-1.5">
            Digital Edition · Studio DH
          </p>
        </div>
      </div>
    </a>
  );
}
